import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * A horizontal swipe, told apart from a scroll and a tap.
 *
 * The first few pixels decide who owns a press: down the page is the scroll's,
 * across is the swipe's, and a diagonal belongs to whichever it is more of. Once
 * a press is known to be horizontal the owner is asked whether it wants a swipe
 * in that direction; one that doesn't lets the press go, so the same surface can
 * swipe one way and not the other. A claimed swipe swallows the click the
 * browser fires when the finger lifts, so ending a swipe on a button doesn't
 * press it.
 */

/** Movement below this is still a tap or a scroll. */
const SLOP_PX = 10;
/** A lift within this long of the last movement carries the finger's speed. */
const FLICK_WINDOW_MS = 90;

export interface SwipeGesture {
  /** Whether this press may become a swipe at all: where it started, and with what. */
  accepts?: (e: ReactPointerEvent) => boolean;
  /** The press has gone horizontal, toward +1 (right) or -1 (left). False lets it go. */
  claim: (direction: 1 | -1) => boolean;
  /** How far the finger has moved across since the press began. */
  move: (dx: number) => void;
  /** The finger lifted: how far it had moved, and its speed then in px/ms. */
  release: (dx: number, velocity: number) => void;
  /** The browser took the press back, or a second finger arrived. */
  cancel: () => void;
}

export function useSwipeGesture(gesture: SwipeGesture): (e: ReactPointerEvent) => void {
  const latest = useRef(gesture);
  latest.current = gesture;

  return useCallback((e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (latest.current.accepts && !latest.current.accepts(e)) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const id = e.pointerId;
    let claimed = false;
    let dx = 0;
    let last = { x: startX, t: e.timeStamp };
    let velocity = 0;

    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('pointerdown', another);
    };

    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      const x = ev.clientX - startX;
      const y = ev.clientY - startY;
      if (!claimed) {
        if (Math.abs(y) > SLOP_PX && Math.abs(y) >= Math.abs(x)) return stop();
        if (Math.abs(x) < SLOP_PX) return;
        if (!latest.current.claim(x > 0 ? 1 : -1)) return stop();
        claimed = true;
      }
      ev.preventDefault();
      dx = x;
      const dt = ev.timeStamp - last.t;
      if (dt > 0) velocity = (ev.clientX - last.x) / dt;
      last = { x: ev.clientX, t: ev.timeStamp };
      latest.current.move(dx);
    };

    const cancel = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      stop();
      if (claimed) latest.current.cancel();
    };

    // A second finger makes this something else: a pinch, or a scroll.
    const another = (ev: PointerEvent) => {
      if (ev.pointerId === id) return;
      stop();
      if (claimed) latest.current.cancel();
    };

    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      stop();
      if (!claimed) return;
      const swallow = (click: MouseEvent) => {
        click.stopPropagation();
        click.preventDefault();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 350);
      // A finger that paused before lifting isn't flicking.
      latest.current.release(dx, ev.timeStamp - last.t < FLICK_WINDOW_MS ? velocity : 0);
    };

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('pointerdown', another);
  }, []);
}

/** Past this share of the distance, or with a flick, a release finishes the move. */
export const COMMIT_SHARE = 0.3;
/** px/ms. */
export const FLICK_SPEED = 0.45;

/** Whether a release at `dx` of `distance`, moving at `velocity`, finishes a move toward `sign`. */
export function commits(dx: number, velocity: number, distance: number, sign: 1 | -1): boolean {
  const along = dx * sign;
  return along > distance * COMMIT_SHARE || (velocity * sign > FLICK_SPEED && along > 30);
}

/** Not a swipe surface: form fields, and anything marked as owning its own horizontal drags. */
export function startsOnOwnDrag(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  return Boolean(el?.closest('input, textarea, select, [data-own-swipe]'));
}
